/**
 * Pure helpers for canonicalising weekday values and deriving a class's
 * selected weekdays from whatever shape the backend returned. Kept in a
 * React-free module so unit tests don't pull in MUI, RTK Query, or any
 * other heavy dependency — mirrors the pattern used by
 * ../CheckStaff/classDetailHeaderHelpers.ts.
 */

const WEEK_ORDER = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

/**
 * Normalise any weekday value (full capitalised "Tuesday", lowercase
 * "tuesday", three-letter "Tue"/"tue", or numeric 0-6) into the exact
 * UI token the day buttons compare against — the full capitalised
 * English name from `WeekDays`. Returns null for anything that cannot
 * be resolved so bad data filters out rather than silently picking
 * Sunday. Keep the alias list in sync with `WeekDays`.
 */
export const normalizeWeekday = (raw: unknown): string | null => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return WEEK_ORDER[raw] ?? null;
  }
  const s = String(raw).trim().toLowerCase();
  if (s === "") return null;
  // Numeric strings count as indices too (0 = Sunday).
  if (/^[0-6]$/.test(s)) return WEEK_ORDER[Number(s)];
  const byName: Record<string, string> = {
    sunday: "Sunday", sun: "Sunday", su: "Sunday",
    monday: "Monday", mon: "Monday", mo: "Monday",
    tuesday: "Tuesday", tue: "Tuesday", tues: "Tuesday", tu: "Tuesday",
    wednesday: "Wednesday", wed: "Wednesday", we: "Wednesday",
    thursday: "Thursday", thu: "Thursday", thur: "Thursday", thurs: "Thursday", th: "Thursday",
    friday: "Friday", fri: "Friday", fr: "Friday",
    saturday: "Saturday", sat: "Saturday", sa: "Saturday",
  };
  return byName[s] ?? null;
};

/**
 * Pull the recurring class's selected weekdays out of whatever shape the
 * load endpoint returned. The /assign/classes/list/fiter payload used by
 * the Edit page does NOT include a flat `selected_days` or `days` field
 * — it returns `availability[]` with one entry per weekday whose
 * `timeSlots` array is non-empty when the class actually runs that day.
 * Other / future endpoints may return the flat shape, so we accept both.
 *
 * All values pass through `normalizeWeekday` so a persisted
 * "monday" / "Mon" / 1 / "Tuesday" all end up as the exact capitalised
 * token (`WeekDays.Tuesday` → "Tuesday"). Returns a deduplicated array
 * in weekday order (Sunday → Saturday).
 */
export const deriveWeekdaysFromClassData = (classData: unknown): string[] => {
  if (!classData || typeof classData !== "object") return [];
  const data = classData as Record<string, unknown>;
  const collected = new Set<string>();

  const push = (raw: unknown) => {
    const canonical = normalizeWeekday(raw);
    if (canonical) collected.add(canonical);
  };

  // 1) Flat shapes (future-proofing / other endpoints).
  const flat = (data.selected_days ?? data.days) as unknown;
  if (Array.isArray(flat)) flat.forEach(push);

  // 2) Nested availability (current list/filter endpoint). The backend
  //    always emits all 7 entries; the ones with populated `timeSlots`
  //    are the real schedule days.
  const availability = data.availability;
  if (Array.isArray(availability)) {
    for (const entry of availability) {
      const slots = (entry as any)?.timeSlots;
      if (Array.isArray(slots) && slots.length > 0) push((entry as any).day);
    }
  }

  return WEEK_ORDER.filter((d) => collected.has(d));
};
